import React, {
  ReactNode, forwardRef, useContext, useMemo, useCallback, MutableRefObject, FC, useRef, ChangeEvent,
} from 'react';

import { WithTranslation } from 'react-i18next';
import DataFormat from '../../format/DataFormat';
import FormColContext from '../_FormColContext';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';

import { StyledInputWrapper, StyledInput, StyledSign, StyledClearButton } from './TextInput.styles';
import setRef from '../../../utils/components/_setRef';
import { InputProps } from '../_Input';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';

export interface TextInputProps extends InputProps {
  /** Ref for the input */
  inputRef?: MutableRefObject<HTMLInputElement>;
  /** Specifies the width of the input itself. */
  inputWidth?: number;
  /** Specify the sign to the left of the input. */
  leftSign?: ReactNode;
  /** Width in pixel of the left sign. */
  leftSignWidth?: number;
  /** Specify the sign to the right of the input. */
  rightSign?: ReactNode;
  /** Width in pixel of the right sign. */
  rightSignWidth?: number;
  /** Make the right sign clickable. */
  clickableRightSign?: boolean;
  /** Make the left sign clickable. */
  clickableLeftSign?: boolean;
  /** Show a clear button. */
  clearButton?: boolean;
  /** Callback when the clear button is clicked. */
  onClear?: () => void;
  /** @ignore */
  textOnly?: boolean;
  /** @ignore */
  isValid?: boolean;
  /** @ignore */
  isTouched?: boolean;
}

interface TextInputInternalProps extends WithTranslation {
  isUnionControlGroup: boolean;
}

export interface TextInputComposedProps extends TextInputProps, TextInputInternalProps {}

const TextInputDefaultProps = {
  leftSignWidth: 24,
  rightSignWidth: 24,
  clickableLeftSign: false,
  clickableRightSign: false,
  clearButton: false,
};

/**
 * @mode developer
 */
const TextInput = Object.assign(
  forwardRef<HTMLInputElement, TextInputComposedProps>(({
    t, inputRef, inputWidth, readOnly, disabled, textOnly, value, leftSign, rightSign, leftSignWidth, rightSignWidth, clickableLeftSign, clickableRightSign, isUnionControlGroup, clearButton, onClear, onChange, ...props
  }: TextInputComposedProps, ref) => {
    const localRef = useRef<HTMLInputElement>();
    const { colId } = useContext(FormColContext);

    const mergeRefs = useCallback((current: HTMLInputElement) => {
      setRef(localRef, current);
      setRef(inputRef, current);
      setRef(ref, current);
      onClear?.();
    }, [inputRef, ref, onClear]);

    const { scheme } = useContext(ColorSchemeContext);

    const tabIndexProps = useMemo(() => (readOnly) ? { tabIndex: -1 } : {}, [readOnly]);

    const handleClear = useCallback(() => {
      localRef.current.value = '';
      localRef.current.focus();
      const event = new Event('change', { bubbles: true });
      Object.defineProperty(event, 'target', { value: localRef.current });
      onChange?.(event as unknown as ChangeEvent<HTMLInputElement>);
    }, [localRef, onChange]);

    if (textOnly) {
      return <DataFormat>{value}</DataFormat>;
    }

    return (
      <StyledInputWrapper inputWidth={inputWidth} isUnionControlGroup={isUnionControlGroup}>
        {leftSign && <StyledSign clickable={clickableLeftSign}>{leftSign}</StyledSign>}
        <StyledInput
          shaded={scheme === COLOR_SCHEMES.SHADE}
          type="text"
          data-testid="TextInput"
          ref={mergeRefs}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          leftSign={leftSign}
          rightSign={rightSign}
          leftSignWidth={leftSignWidth}
          rightSignWidth={rightSignWidth}
          clearButton={clearButton}
          aria-labelledby={colId}
          onChange={onChange}
          {...props}
          {...tabIndexProps}
        />
        {clearButton && (
          <StyledClearButton
            Icon={CloseIcon}
            onClick={handleClear}
            label={t('clear')}
            rightSignWidth={rightSignWidth}
            rightSign={rightSign}
          />
        )}
        {rightSign && <StyledSign right clickable={clickableRightSign}>{rightSign}</StyledSign>}
      </StyledInputWrapper>
    );
  }) as FC<TextInputComposedProps>,
  {
    defaultProps: TextInputDefaultProps,
    labeledControl: true,
    displayName: 'TextInput',
    StyledInput,
    StyledSign,
  },
);

export default TextInput;
